import { IVectorLayerGroupConfig } from "@esdm/map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource } from "@esdm/map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "@esdm/map/dist/layers/vector/VectorLayerStrategy";

import { SuffolkRecordType, SuffolkRecordSubType} from "./SuffolkRecordTypes";
import { IWmsLayerGroupConfig } from "@esdm/map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";
import { WfsBboxAsWktRequestParams } from "@esdm/map/dist/layers/vector/sources/wfs/WfsBboxAsWktRequestParams";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/MapServer/Hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: SuffolkRecordType,
        recordSubType: SuffolkRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case SuffolkRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;

            case SuffolkRecordType.Event:
                layerConfig = this.Event(opts);
                break;

            case SuffolkRecordType.Consultation:
                layerConfig = this.Consultation(opts);
                break;

            case SuffolkRecordType.SuffolkBoundary:
                layerConfig = this.SuffolkBoundary();
                break;

        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case SuffolkRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case SuffolkRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case SuffolkRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case SuffolkRecordSubType.Shine:
                    layerConfig = this.Shine(opts);
                    break; 

            }
        }

        return layerConfig;
    }

    private SuffolkBoundary = (): IWmsLayerGroupConfig => {
        return {
            displayName: "Suffolk Boundary",
            ref: "SuffolkBoundary",
            type: SuffolkRecordType.SuffolkBoundary,            
            url: this.mapFileUrl,
            displayInLayerSwitcher: false,
            displayInLegend: false,
            visibleOnLoad: true,  
            queryable: false,
            layers: [
                {
                    layerName: "SuffolkBoundaryWMS",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: SuffolkRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 12,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 12,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    },
                    {
                        layerName: "MonumentPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: SuffolkRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 12,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 12,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    },
                    {
                        layerName: "EventPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }
                ]
            }, ...opts
        };
    }

    private Consultation = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Consultations",
                ref: "Consultations",
                type: SuffolkRecordType.Consultation,                
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,                
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "ConsultationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "ConsultationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "ConsultationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "ConsultationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "ConsultationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: SuffolkRecordType.Designation,
                subType: SuffolkRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: SuffolkRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: SuffolkRecordType.Designation,
                subType: SuffolkRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: SuffolkRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },                   
                   
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: SuffolkRecordType.Designation,
                subType: SuffolkRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: SuffolkRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },                                     
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private Shine = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "SHINE",
                ref: "Shine",
                type: SuffolkRecordType.Designation,
                subType: SuffolkRecordSubType.Shine,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: SuffolkRecordSubType.Shine },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }


}

